import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EMPTY_FORM = {
  client: null,
  site: null,
  siteName: '',
  description: '',
  primaryUser: null,
  secondaryUser: null,
  itemId: null,
};

const getStaffLabel = (user = {}) => {
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return fullName || user.email || 'Non renseigne';
};

const getEntityId = (entity = {}) => String(entity._id || '');

const normalizePortfolio = (portfolio = []) =>
  (Array.isArray(portfolio) ? portfolio : [])
    .map((entry, index) => ({
      user: entry.user?._id || entry.user || entry.userId,
      userDetails: entry.user,
      priority: Number(entry.priority || index + 1),
    }))
    .filter((entry) => entry.user)
    .sort((a, b) => a.priority - b.priority)
    .map((entry, index) => ({ ...entry, priority: index + 1 }));

const getPortfolioUser = (portfolio, priority) => {
  const entry = normalizePortfolio(portfolio).find((row) => row.priority === priority);
  return entry?.userDetails || null;
};

const getPortfolioUserId = (portfolio, priority) => {
  const entry = normalizePortfolio(portfolio).find((row) => row.priority === priority);
  return entry ? String(entry.user) : '';
};

const getGroupLabel = (entity = {}) => entity.portfolioGroup || '-';
const getSectorLabel = (entity = {}) => {
  if (Array.isArray(entity.organizationTypes) && entity.organizationTypes.length) {
    return entity.organizationTypes.join(', ');
  }
  return '-';
};

const buildStaffOption = (user) => ({
  ...user,
  value: String(user._id),
  label: getStaffLabel(user),
});

const colorForIndex = (index) => {
  const colors = ['#2f5597', '#4472c4', '#1f4e79', '#5b9bd5', '#385723', '#7030a0'];
  return colors[index % colors.length];
};

const getSiteLabel = (item) => item.site?.name || item.siteName || 'General';

function ManagerCell({ user, fallback = '-' }) {
  if (!user) return <Typography variant="body2" color="text.secondary">{fallback}</Typography>;

  return (
    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
      {getStaffLabel(user)}
    </Typography>
  );
}

function PortfolioMatrix({ title, rows, color, onEdit, onClear }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderColor: 'divider' }}>
      <Box sx={{ bgcolor: color, color: '#fff', px: 1.5, py: 0.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>{title}</Typography>
          <Chip size="small" label={`${rows.length} ligne${rows.length > 1 ? 's' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
        </Stack>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#eaf2f8' }}>
              <TableCell sx={{ width: '13%', fontWeight: 700 }}>Groupe</TableCell>
              <TableCell sx={{ width: '20%', fontWeight: 700 }}>Client</TableCell>
              <TableCell sx={{ width: '15%', fontWeight: 700 }}>Site</TableCell>
              <TableCell sx={{ width: '17%', fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ width: '13%', fontWeight: 700 }}>Secteur</TableCell>
              <TableCell sx={{ width: '12%', fontWeight: 700 }}>Principal</TableCell>
              <TableCell sx={{ width: '10%', fontWeight: 700 }}>Binome</TableCell>
              <TableCell align="right" sx={{ width: 80, fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.rowId} hover>
                <TableCell><Typography variant="body2" noWrap>{getGroupLabel(row.client)}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{row.client.name || 'Sans nom'}</Typography></TableCell>
                <TableCell><Typography variant="body2" noWrap>{getSiteLabel(row.item)}</Typography></TableCell>
                <TableCell><Typography variant="body2" color={row.item.description ? 'text.primary' : 'text.secondary'} noWrap>{row.item.description || '-'}</Typography></TableCell>
                <TableCell><Typography variant="body2" noWrap>{getSectorLabel(row.client)}</Typography></TableCell>
                <TableCell><ManagerCell user={getPortfolioUser(row.item.staffPortfolio, 1)} /></TableCell>
                <TableCell><ManagerCell user={getPortfolioUser(row.item.staffPortfolio, 2)} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => onEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {row.item._id ? (
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => onClear(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function StaffPortfolioPage() {
  const [summary, setSummary] = useState({ organizations: [], contracts: [] });
  const [staffUsers, setStaffUsers] = useState([]);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [siteOptions, setSiteOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [managerFilter, setManagerFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const staffOptions = useMemo(() => staffUsers.map(buildStaffOption), [staffUsers]);

  const clientOptions = useMemo(
    () => (summary.organizations || [])
      .map((row) => row.entity)
      .filter(Boolean)
      .map((entity) => ({ ...entity, value: getEntityId(entity), label: entity.name || 'Sans nom' })),
    [summary.organizations]
  );

  const portfolioRows = useMemo(() => {
    const rows = [];

    clientOptions.forEach((client) => {
      const items = Array.isArray(client.staffPortfolioItems) ? client.staffPortfolioItems : [];

      if (items.length) {
        items.forEach((item) => {
          rows.push({
            rowId: `${client.value}-${item._id}`,
            client,
            item,
          });
        });
        return;
      }

      const legacyPortfolio = normalizePortfolio(client.staffPortfolio);
      if (legacyPortfolio.length) {
        rows.push({
          rowId: `${client.value}-legacy`,
          client,
          item: {
            _id: null,
            site: null,
            siteName: '',
            description: '',
            staffPortfolio: legacyPortfolio,
          },
        });
      }
    });

    return rows;
  }, [clientOptions]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return portfolioRows.filter((row) => {
      const primaryId = getPortfolioUserId(row.item.staffPortfolio, 1);
      const secondaryId = getPortfolioUserId(row.item.staffPortfolio, 2);
      const text = [
        row.client.name,
        getGroupLabel(row.client),
        getSectorLabel(row.client),
        getSiteLabel(row.item),
        row.item.description,
      ].join(' ').toLowerCase();
      const matchesSearch = !normalizedSearch || text.includes(normalizedSearch);
      const matchesManager = managerFilter === 'all'
        || primaryId === managerFilter
        || secondaryId === managerFilter;

      return matchesSearch && matchesManager;
    });
  }, [managerFilter, portfolioRows, search]);

  const groupedPortfolios = useMemo(() => {
    const groups = new Map();

    filteredRows.forEach((row) => {
      const primary = getPortfolioUser(row.item.staffPortfolio, 1);
      const primaryId = getPortfolioUserId(row.item.staffPortfolio, 1) || 'unassigned';
      const title = primary ? getStaffLabel(primary) : 'Sans compte principal';

      if (!groups.has(primaryId)) {
        groups.set(primaryId, { id: primaryId, title, rows: [] });
      }

      groups.get(primaryId).rows.push(row);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.id === 'unassigned') return 1;
      if (b.id === 'unassigned') return -1;
      return a.title.localeCompare(b.title);
    });
  }, [filteredRows]);

  const stats = useMemo(() => ({
    totalClients: clientOptions.length,
    totalRows: portfolioRows.length,
    detailedRows: portfolioRows.filter((row) => row.item._id).length,
  }), [clientOptions.length, portfolioRows]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryResponse, staffResponse] = await Promise.all([
        api.get('/api/staff-portfolio/summary'),
        api.get('/api/staff-portfolio/staff-options'),
      ]);
      setSummary(summaryResponse.data || { organizations: [], contracts: [] });
      setStaffUsers(Array.isArray(staffResponse.data) ? staffResponse.data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadSites = async (clientId) => {
    if (!clientId) {
      setSiteOptions([]);
      return;
    }

    setSitesLoading(true);
    try {
      const { data } = await api.get(`/api/staff-portfolio/organizations/${clientId}/sites`);
      setSiteOptions((Array.isArray(data) ? data : []).map((site) => ({
        ...site,
        value: String(site._id),
        label: site.address ? `${site.name || 'Site'} - ${site.address}` : site.name || 'Site',
      })));
    } catch (e) {
      setSiteOptions([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSiteOptions([]);
  };

  const closeAssignment = () => {
    if (saving) return;
    setAssignmentOpen(false);
    resetForm();
  };

  const openAssignment = () => {
    resetForm();
    setAssignmentOpen(true);
  };

  const openEdit = (row) => {
    const primaryId = getPortfolioUserId(row.item.staffPortfolio, 1);
    const secondaryId = getPortfolioUserId(row.item.staffPortfolio, 2);
    const client = clientOptions.find((option) => option.value === row.client.value) || row.client;

    setForm({
      client,
      site: row.item.site?._id ? { ...row.item.site, value: String(row.item.site._id), label: row.item.site.address ? `${row.item.site.name || 'Site'} - ${row.item.site.address}` : row.item.site.name || 'Site' } : null,
      siteName: row.item.siteName || row.item.site?.name || '',
      description: row.item.description || '',
      primaryUser: staffOptions.find((option) => option.value === primaryId) || null,
      secondaryUser: staffOptions.find((option) => option.value === secondaryId) || null,
      itemId: row.item._id || null,
    });
    loadSites(client.value);
    setAssignmentOpen(true);
  };

  const saveItem = async () => {
    if (!form.client?.value || !form.primaryUser) {
      setError('Client et compte principal requis.');
      return;
    }

    setSaving(true);
    setSuccess('');
    try {
      const payload = {
        siteId: form.site?.value || null,
        siteName: form.siteName,
        description: form.description,
        staffPortfolio: [form.primaryUser, form.secondaryUser].filter(Boolean).map((user, index) => ({
          user: user.value || user._id,
          priority: index + 1,
        })),
      };

      if (form.itemId) {
        await api.put(`/api/staff-portfolio/organizations/${form.client.value}/items/${form.itemId}`, payload);
      } else {
        await api.post(`/api/staff-portfolio/organizations/${form.client.value}/items`, payload);
      }

      setSuccess(`Ligne portefeuille mise a jour pour ${form.client.name || form.client.label}`);
      setAssignmentOpen(false);
      resetForm();
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (row) => {
    if (!row.item._id) return;

    setSaving(true);
    try {
      await api.delete(`/api/staff-portfolio/organizations/${row.client.value}/items/${row.item._id}`);
      setSuccess('Ligne portefeuille supprimee.');
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <GroupsIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>Portefeuille client</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Lignes par client, site ou périmètre.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={`${stats.totalRows} lignes`} color="primary" variant="outlined" />
          <Chip label={`${stats.totalClients} clients`} variant="outlined" />
          <Chip label={`${stats.detailedRows} detaillees`} color={stats.detailedRows ? 'success' : 'default'} variant="outlined" />
          <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>Actualiser</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openAssignment}>Ajouter</Button>
        </Stack>
      </Stack>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            label="Filtrer"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { md: 320 } }}
          />
          <TextField
            select
            size="small"
            label="Compte"
            value={managerFilter}
            onChange={(event) => setManagerFilter(event.target.value)}
            sx={{ minWidth: { md: 260 } }}
          >
            <MenuItem value="all">Tous les comptes</MenuItem>
            {staffOptions.map((staff) => (
              <MenuItem key={staff.value} value={staff.value}>{staff.label}</MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
            {filteredRows.length} ligne{filteredRows.length > 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {groupedPortfolios.map((group, index) => (
          <PortfolioMatrix
            key={group.id}
            title={group.title}
            rows={group.rows}
            color={group.id === 'unassigned' ? '#7f7f7f' : colorForIndex(index)}
            onEdit={openEdit}
            onClear={deleteItem}
          />
        ))}
        {!groupedPortfolios.length ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">Aucune ligne portefeuille.</Typography>
          </Paper>
        ) : null}
      </Stack>

      <Dialog open={assignmentOpen} onClose={closeAssignment} fullWidth maxWidth="sm">
        <DialogTitle>{form.itemId ? 'Modifier une ligne' : 'Ajouter une ligne'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Autocomplete
              options={clientOptions}
              value={form.client}
              getOptionLabel={(option) => option.label || option.name || 'Sans nom'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => {
                setForm({ ...form, client: value, site: null, siteName: '' });
                loadSites(value?.value);
              }}
              renderInput={(params) => <TextField {...params} label="Client" size="small" />}
            />

            <Autocomplete
              options={siteOptions}
              value={form.site}
              loading={sitesLoading}
              getOptionLabel={(option) => option.label || option.name || 'Site'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setForm({ ...form, site: value, siteName: value?.name || form.siteName })}
              renderInput={(params) => <TextField {...params} label="Site Hudu" size="small" />}
            />

            <TextField
              label="Site / périmètre"
              size="small"
              value={form.siteName}
              onChange={(event) => setForm({ ...form, siteName: event.target.value })}
              helperText="Libre si le site n'est pas dans Hudu"
            />

            <TextField
              label="Description"
              size="small"
              multiline
              minRows={2}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />

            <Autocomplete
              options={staffOptions}
              value={form.primaryUser}
              getOptionLabel={(option) => option.label || getStaffLabel(option)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => {
                setForm({
                  ...form,
                  primaryUser: value,
                  secondaryUser: value?.value === form.secondaryUser?.value ? null : form.secondaryUser,
                });
              }}
              renderInput={(params) => <TextField {...params} label="Compte principal" size="small" />}
            />

            <Autocomplete
              options={staffOptions.filter((option) => option.value !== form.primaryUser?.value)}
              value={form.secondaryUser}
              getOptionLabel={(option) => option.label || getStaffLabel(option)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setForm({ ...form, secondaryUser: value })}
              renderInput={(params) => <TextField {...params} label="Binome" size="small" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignment} disabled={saving}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
            onClick={saveItem}
            disabled={saving || !form.client || !form.primaryUser}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
